import { ICON_PROPS, type OutlookEmailContext, cleanSubject, formatRelative, friendlyAiError, postToHost, runPreSendCheck, sentimentColor, shortDate, tierColor } from './shared.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api } from '../../api.js';
import { AddToCaseButton, AttachmentsBadge, BulkAddToCategoryMenu, ReplyComposer, SearchScopeBanner, SecurityBadge, SnoozeButton } from './shared-components.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — search panel + rows + clusters + triage,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export interface SearchHit {
  id: string;
  message_id: string;
  subject: string | null;
  from_addr: string;
  received_at: string;
  classification_tier: string | null;
  folder?: string | null;
  snippet: string;
  /** When `Collapse dupes` is on, this row absorbs N other identical rows. */
  dup_count?: number;
}

export const QUICK_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'P0', label: 'P0', color: 'red' },
  { value: 'P1', label: 'P1', color: 'orange' },
  { value: 'P2', label: 'P2', color: 'yellow' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'This week' },
  { value: '30d', label: 'Last 30 days' },
] as const;

// Search clustering — renders the threads + topics + singletons response
// from /addin/search/cluster as a compact, browsable card.
export function ClusteredResultsCard({
  data,
  busy,
  onOpenMessage,
}: {
  data: Awaited<ReturnType<typeof api.addinSearchCluster>> | null;
  busy: boolean;
  onOpenMessage: (message_id: string) => void;
}): React.JSX.Element {
  if (busy && !data) return <Center p="md"><Loader size="sm" /></Center>;
  if (!data) return <></>;
  if (data.total_hits === 0) {
    return (
      <Card padding="sm" radius="md" withBorder>
        <Text size="sm" c="dimmed">No hits to cluster.</Text>
      </Card>
    );
  }
  return (
    <Card padding="sm" radius="md" withBorder
      style={{ borderLeft: '4px solid var(--mantine-color-indigo-6)' }}>
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <IconUsersGroup {...ICON_PROPS} />
            <Text fw={600} size="sm">
              Clustered: {data.counts.threads} thread{data.counts.threads === 1 ? '' : 's'} ·{' '}
              {data.counts.topics} topic{data.counts.topics === 1 ? '' : 's'} ·{' '}
              {data.counts.singletons} singleton{data.counts.singletons === 1 ? '' : 's'}
            </Text>
          </Group>
          <Group gap={4}>
            <Button size="compact-xs" variant="subtle" leftSection={<IconFileText size={12} />}
              onClick={() => {
                const esc = (s: string | number | null | undefined) => {
                  if (s == null) return '';
                  const v = String(s);
                  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                };
                const lines = ['kind,group_id,subject_or_topic,participants_or_senders,msg_count,first_at,last_at,tier_top'];
                for (const t of data.threads) {
                  lines.push([t.kind, t.group_id, t.subject, t.participants.join('|'), t.msg_count, t.first_at, t.last_at, t.tier_top].map(esc).join(','));
                }
                for (const t of data.topics) {
                  lines.push([t.kind, t.group_id, t.topic_label, t.senders.join('|'), t.msg_count, t.first_at, t.last_at, t.tier_top].map(esc).join(','));
                }
                const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `search-cluster-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}>
              CSV
            </Button>
            <Text size="xs" c="dimmed">{data.total_hits} hits</Text>
          </Group>
        </Group>

        {data.threads.length > 0 && (
          <Stack gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase">Threads</Text>
            {data.threads.slice(0, 8).map((t) => (
              <Paper key={t.group_id} p="xs" withBorder>
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={4} wrap="nowrap">
                      {t.tier_top && (
                        <Badge size="xs" color={
                          t.tier_top === 'P0' ? 'red' : t.tier_top === 'P1' ? 'orange' :
                          t.tier_top === 'P2' ? 'yellow' : 'gray'
                        }>{t.tier_top}</Badge>
                      )}
                      <Text size="sm" fw={500} lineClamp={1}>{t.subject}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {t.msg_count} msg · {t.participant_count} participant{t.participant_count === 1 ? '' : 's'} ·
                      last {formatRelative(t.last_at)}
                    </Text>
                  </Stack>
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <Button size="compact-xs" variant="default">
                        Open ({t.sample_message_ids.length})
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {t.sample_message_ids.map((mid, i) => (
                        <Menu.Item key={mid} onClick={() => onOpenMessage(mid)}>
                          Message {i + 1}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}

        {data.topics.length > 0 && (
          <Stack gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase">Topics</Text>
            {data.topics.slice(0, 8).map((t) => (
              <Paper key={t.group_id} p="xs" withBorder>
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={4} wrap="nowrap">
                      {t.tier_top && (
                        <Badge size="xs" color={
                          t.tier_top === 'P0' ? 'red' : t.tier_top === 'P1' ? 'orange' :
                          t.tier_top === 'P2' ? 'yellow' : 'gray'
                        }>{t.tier_top}</Badge>
                      )}
                      <Text size="sm" fw={500} lineClamp={1}>{t.topic_label}</Text>
                      <Badge size="xs" variant="dot" color="grape">@{t.sender_domain}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {t.msg_count} msg · {t.senders.length} sender{t.senders.length === 1 ? '' : 's'} ·
                      last {formatRelative(t.last_at)}
                    </Text>
                  </Stack>
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <Button size="compact-xs" variant="default">
                        Open ({t.sample_message_ids.length})
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {t.sample_message_ids.map((mid, i) => (
                        <Menu.Item key={mid} onClick={() => onOpenMessage(mid)}>
                          Message {i + 1}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}

        {data.singletons.length > 0 && data.threads.length + data.topics.length === 0 && (
          <Text size="xs" c="dimmed">
            All {data.singletons.length} hits are standalone — nothing to cluster. Hide cluster view to see them.
          </Text>
        )}
      </Stack>
    </Card>
  );
}

export function SearchPanel({
  onOpenSender,
  seedFilter,
  onSeedConsumed,
}: {
  onOpenSender?: (email: string) => void;
  seedFilter?: { q?: string; tier?: string; sender?: string; since?: string } | null;
  onSeedConsumed?: () => void;
} = {}): React.JSX.Element {
  const [q, setQ] = useState(seedFilter?.q ?? '');
  // Round 29: pick up filter that was set by InsightsPanel cards via
  // sessionStorage (so clicking "Critical 7d" lands on Search with the P0
  // chip already engaged).
  const [filter, setFilter] = useState<string>(() => {
    if (seedFilter?.tier) return seedFilter.tier;
    const f = sessionStorage.getItem('addin_search_filter');
    if (f) {
      sessionStorage.removeItem('addin_search_filter');
      return f;
    }
    return 'all';
  });
  // Round 34: bulk multi-select. Renamed to selectedIds to avoid clobbering
  // `selected` (the SearchHit shown in the preview pane).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Muted-sender state for the search banner. mutedSenders tracks addresses
  // the user just muted (so the banner hides immediately + their hits drop
  // out of view). dismissedSenders tracks ones the user X'd without muting.
  const [mutedSenders, setMutedSenders] = useState<Set<string>>(new Set());
  const [dismissedSenders, setDismissedSenders] = useState<Set<string>>(new Set());

  // Apply seed filter from command-bar / sender-drawer once, then clear.
  useEffect(() => {
    if (seedFilter) {
      if (seedFilter.q != null) setQ(seedFilter.q);
      if (seedFilter.tier) setFilter(seedFilter.tier);
      if (seedFilter.sender) setSender(seedFilter.sender);
      onSeedConsumed?.();
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: seed apply once
  }, [seedFilter]);
  const [sender, setSender] = useState('');
  // Round 43: scope = subject only / sender only / body only / all four together.
  const [scope, setScope] = useState<'all' | 'subject' | 'sender' | 'body'>('all');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [searchTime, setSearchTime] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  // Search clustering — toggle on to group hits by thread + topic.
  const [clusterMode, setClusterMode] = useState<boolean>(() => {
    try { return localStorage.getItem('addin_search_cluster') === '1'; } catch { return false; }
  });
  const [clusterData, setClusterData] = useState<Awaited<ReturnType<typeof api.addinSearchCluster>> | null>(null);
  const [clusterBusy, setClusterBusy] = useState(false);
  useEffect(() => { try { localStorage.setItem('addin_search_cluster', clusterMode ? '1' : '0'); } catch { /* ignore */ } }, [clusterMode]);
  const [collapseDupes, setCollapseDupes] = useState<boolean>(() => {
    try { return localStorage.getItem('addin_search_collapse') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('addin_search_collapse', collapseDupes ? '1' : '0'); } catch { /* ignore */ } }, [collapseDupes]);
  const [folderFilter, setFolderFilter] = useState<string | null>(null);
  const [dateSince, setDateSince] = useState('');
  const [dateUntil, setDateUntil] = useState('');
  // Risk-feed lookup — fetched once on mount, exposed as Map for per-row badges.
  const [riskMap, setRiskMap] = useState<Map<string, { score: number; verdict: 'clean' | 'suspicious' | 'dangerous'; top_signal: string }>>(new Map());
  // Case membership — fetched once on mount, exposed as Map for per-row badges.
  const [caseMap, setCaseMap] = useState<Map<string, { case_id: string; case_name: string; status: string }>>(new Map());
  // Full cases list — for the "Add to case" bulk menu (covers empty cases too).
  const [allCases, setAllCases] = useState<Awaited<ReturnType<typeof api.addinCasesList>>['cases']>([]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
  useEffect(() => {
    // Round 29: align the risk-feed cache key with the splash pre-fetch
    // (which uses 14 days, limit=30). Previously we asked for 30 days,
    // which doesn't hit the splash-warmed cache → forced a 17-22s SQL
    // scan on every cold mount, blocking the brain event loop and
    // queueing every other request behind it (search, bulk-index POSTs,
    // even health checks). 14 days is plenty for risk badges on search
    // hits — the panel surfaces "today's most relevant" mail, not
    // historical archive risk.
    //
    // Also defer by 800ms: TabKeepAlive mounts every panel up-front, so
    // splash's parallel waterfall already hammered the brain — give it
    // a beat before piling on the search-panel-specific badges fetch.
    const t = setTimeout(() => {
      api.addinRiskFeed(14, 30, 0).then((r) => {
        const m = new Map<string, { score: number; verdict: 'clean' | 'suspicious' | 'dangerous'; top_signal: string }>();
        for (const it of r.items) {
          m.set(it.message_id, {
            score: it.score, verdict: it.verdict,
            top_signal: it.signals[0]?.type.replace(/_/g, ' ') ?? 'risk',
          });
        }
        setRiskMap(m);
      }).catch(() => { /* silent — risk badges are best-effort */ });
      api.addinCasesMembership().then((r) => {
        const m = new Map<string, { case_id: string; case_name: string; status: string }>();
        for (const x of r.memberships) m.set(x.message_id, { case_id: x.case_id, case_name: x.case_name, status: x.status });
        setCaseMap(m);
      }).catch(() => { /* silent */ });
      api.addinCasesList().then((r) => setAllCases(r.cases)).catch(() => { /* silent */ });
    }, 800);
    return () => clearTimeout(t);
  }, []);
  const [selected, setSelected] = useState<SearchHit | null>(null);
  const [detail, setDetail] = useState<{
    body: string;
    body_unavailable?: boolean;
    classification_tier: string | null;
    classification_confidence: number | null;
  } | null>(null);
  const [draft, setDraft] = useState('');
  const [draftBusy, setDraftBusy] = useState(false);
  const [analyzeBusy, setAnalyzeBusy] = useState(false);
  const [analyzeOut, setAnalyzeOut] = useState<Awaited<ReturnType<typeof api.addinAnalyze>> | null>(
    null,
  );
  const [folderList, setFolderList] = useState<
    Array<{ path: string; name: string; depth: number }>
  >([]);
  const seqRef = useRef(0);

  // Pull current folder list from host for the Move-to dropdown.
  // Round 29: removeEventListener IS supported on chrome.webview — pre-fix
  // every SearchPanel mount/unmount cycle leaked a 'message' listener.
  // With TabKeepAlive's lazy-mount they stay alive after first visit, but
  // any future re-mount path (HMR, error boundary recovery) still leaked.
  // Bridge events fire 10-30/sec during indexing; multiple stale listeners
  // = exponential setState calls on every event, slowing the UI.
  useEffect(() => {
    const handler = (e: { data: unknown }) => {
      try {
        const obj = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (obj && typeof obj === 'object' && obj.type === 'outlook_folders') {
          setFolderList(
            (obj.folders as Array<{ path: string; name: string; depth: number }>) ?? [],
          );
        }
      } catch {
        /* ignore */
      }
    };
    const wv = window.chrome?.webview;
    wv?.addEventListener('message', handler);
    postToHost({ type: 'list_outlook_folders' });
    return () => {
      try { wv?.removeEventListener('message', handler); } catch { /* ignore */ }
    };
  }, []);

  const filterParams = useMemo(() => {
    const p: { tier?: string; since?: string; until?: string; folder?: string } = {};
    const now = Date.now();
    if (filter === 'P0' || filter === 'P1' || filter === 'P2' || filter === 'P3' || filter === 'P4')
      p.tier = filter;
    if (filter === 'today') p.since = new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
    if (filter === '7d') p.since = new Date(now - 7 * 86400000).toISOString();
    if (filter === '30d') p.since = new Date(now - 30 * 86400000).toISOString();
    // Date range picker overrides preset chip when set.
    if (dateSince) p.since = new Date(`${dateSince}T00:00:00`).toISOString();
    if (dateUntil) p.until = new Date(`${dateUntil}T23:59:59`).toISOString();
    if (folderFilter) p.folder = folderFilter;
    return p;
  }, [filter, dateSince, dateUntil, folderFilter]);

  // Run search whenever query, filter, or sender changes (debounced).
  // Round 28-W: 350ms debounce + AbortController so a fast typer doesn't
  // fire one request per character. Pre-fix: typing "ITW WASHINGTON"
  // fired 17 parallel requests — even with the seqRef sequence guard,
  // every request hit Caddy + brain because there was no cancel signal.
  useEffect(() => {
    const my = ++seqRef.current;
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setBusy(true);
      const t0 = performance.now();
      try {
        let hits: SearchHit[];
        if (scope !== 'all' && q.trim()) {
          const r = await api.addinSearchScoped({
            q: q.trim(),
            scope,
            ...(filterParams.tier ? { tier: filterParams.tier } : {}),
            ...(sender.trim() ? { sender: sender.trim() } : {}),
            limit: 100,
          }, ctrl.signal);
          hits = r.hits.map((h) => ({ ...h, snippet: h.snippet ?? '' })) as SearchHit[];
        } else {
          const r = await api.addinSearchAdvanced({
            ...(q.trim() ? { q: q.trim() } : {}),
            ...filterParams,
            ...(sender.trim() ? { sender: sender.trim() } : {}),
            limit: 100,
          }, ctrl.signal);
          hits = r.hits;
        }
        if (my !== seqRef.current) return;
        setHits(hits);
        setSearchTime(performance.now() - t0);
      } catch (e) {
        // Aborted requests throw — silently ignore (the next typed char
        // already fired a fresh search).
        if (e instanceof Error && (e.name === 'AbortError' || /aborted/i.test(e.message))) return;
        if (my !== seqRef.current) return;
        notifications.show({
          color: 'red',
          message: e instanceof Error ? e.message : 'Search failed',
        });
      } finally {
        if (my === seqRef.current) setBusy(false);
      }
    }, 350);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q, filterParams, sender, scope]);

  // Cluster fetch — only runs when clusterMode is on. Same query params as
  // the flat search; uses a separate window/limit cap on the brain side.
  // biome-ignore lint/correctness/useExhaustiveDependencies: clusterMode + query inputs
  useEffect(() => {
    if (!clusterMode) { setClusterData(null); return; }
    let cancelled = false;
    setClusterBusy(true);
    api
      .addinSearchCluster({
        ...(q.trim() ? { q: q.trim() } : {}),
        ...filterParams,
        ...(sender.trim() ? { sender: sender.trim() } : {}),
        limit: 300,
      })
      .then((r) => { if (!cancelled) setClusterData(r); })
      .catch((e) => {
        if (!cancelled) notifications.show({
          color: 'red',
          message: e instanceof Error ? e.message : 'Cluster failed',
        });
      })
      .finally(() => { if (!cancelled) setClusterBusy(false); });
    return () => { cancelled = true; };
  }, [clusterMode, q, filterParams, sender]);

  // Query history — last 10 distinct non-empty search queries.
  const [queryHistory, setQueryHistory] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('addin_search_query_history') ?? '[]') as string[]; }
    catch { return []; }
  });
  function pushHistory(qIn: string) {
    const v = qIn.trim();
    if (v.length < 2) return;
    setQueryHistory((prev) => {
      const next = [v, ...prev.filter((x) => x !== v)].slice(0, 10);
      localStorage.setItem('addin_search_query_history', JSON.stringify(next));
      return next;
    });
  }
  // Push on debounce-settled queries.
  // biome-ignore lint/correctness/useExhaustiveDependencies: q only
  useEffect(() => {
    const t = setTimeout(() => pushHistory(q), 1500);
    return () => clearTimeout(t);
  }, [q]);

  // URL-hash filter share — encode current filters into the URL hash so a
  // colleague can paste the link and land on the same view. Hash format:
  //   #addin-search=<base64 of {q,filter,sender,scope}>
  function shareFilterUrl() {
    const payload = { q: q.trim(), filter, sender: sender.trim(), scope };
    const enc = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    const base = `${window.location.origin}${window.location.pathname}`;
    const url = `${base}#addin-search=${enc}`;
    navigator.clipboard.writeText(url)
      .then(() => notifications.show({ message: 'Filter URL copied to clipboard.' }))
      .catch(() => notifications.show({ color: 'red', message: 'Could not copy. URL: ' + url }));
  }
  // On mount, hydrate from #addin-search if present (one-shot).
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
  useEffect(() => {
    const h = window.location.hash;
    const m = h.match(/^#addin-search=(.+)$/);
    if (!m) return;
    try {
      const parsed = JSON.parse(decodeURIComponent(escape(atob(m[1]!)))) as {
        q?: string; filter?: string; sender?: string; scope?: string;
      };
      if (typeof parsed.q === 'string') setQ(parsed.q);
      if (typeof parsed.filter === 'string') setFilter(parsed.filter);
      if (typeof parsed.sender === 'string') setSender(parsed.sender);
      if (typeof parsed.scope === 'string') setScope(parsed.scope as never);
      // Clear the hash so a refresh doesn't re-apply.
      history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      notifications.show({ message: 'Search filters loaded from shared link.' });
    } catch { /* ignore corrupt hash */ }
  }, []);

  // Saved filter presets — name + recall a (q, filter, sender, scope) tuple.
  // Stored client-side in localStorage; small enough to skip a brain round-trip.
  type SearchPreset = { id: string; name: string; q: string; filter: string; sender: string; scope: string };
  const [presets, setPresets] = useState<SearchPreset[]>(() => {
    try { return JSON.parse(localStorage.getItem('addin_search_presets') ?? '[]') as SearchPreset[]; }
    catch { return []; }
  });
  function persistPresets(next: SearchPreset[]) {
    setPresets(next);
    localStorage.setItem('addin_search_presets', JSON.stringify(next));
  }
  function savePreset() {
    const name = prompt('Preset name:');
    if (!name?.trim()) return;
    const p: SearchPreset = {
      id: `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: name.trim(),
      q: q.trim(), filter, sender: sender.trim(), scope,
    };
    persistPresets([...presets, p]);
    notifications.show({ message: `Saved preset "${p.name}".` });
  }
  function applyPreset(p: SearchPreset) {
    setQ(p.q);
    setFilter(p.filter);
    setSender(p.sender);
    setScope(p.scope as never);
  }
  function deletePreset(id: string) {
    persistPresets(presets.filter((p) => p.id !== id));
  }
  function exportPresets() {
    const blob = new Blob([JSON.stringify(presets, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `addin-search-presets-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  function importPresets() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(String(reader.result)) as SearchPreset[];
          if (!Array.isArray(parsed)) throw new Error('Expected array');
          // Merge by name; new entries get fresh ids.
          const byName = new Map(presets.map((p) => [p.name, p]));
          for (const p of parsed) {
            if (!p?.name) continue;
            byName.set(p.name, {
              id: p.id ?? `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              name: p.name,
              q: p.q ?? '', filter: p.filter ?? 'all',
              sender: p.sender ?? '', scope: p.scope ?? 'all',
            });
          }
          persistPresets([...byName.values()]);
          notifications.show({ color: 'green', message: `Imported ${parsed.length} preset${parsed.length === 1 ? '' : 's'}` });
        } catch (e) {
          notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Bad JSON' });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  // Keyboard navigation: j/k move selection, Enter opens, x toggles bulk
  // select, / focuses query, ? shows help. Ignored while typing in inputs.
  const [helpOpen, setHelpOpen] = useState(false);
  const queryInputRef = useRef<HTMLInputElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-bind when hits change
  useEffect(() => {
    function isTyping() {
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || (document.activeElement as HTMLElement | null)?.isContentEditable;
    }
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === '/' && !isTyping()) {
        e.preventDefault();
        queryInputRef.current?.focus();
        return;
      }
      if (isTyping()) return;
      if (e.key === '?') { e.preventDefault(); setHelpOpen(true); return; }
      if (hits.length === 0) return;
      const idx = selected ? hits.findIndex((h) => h.id === selected.id) : -1;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = hits[Math.min(hits.length - 1, idx + 1)];
        if (next) selectHit(next);
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = hits[Math.max(0, idx - 1)];
        if (next) selectHit(next);
      } else if (e.key === 'Enter' && selected) {
        e.preventDefault();
        postToHost({ type: 'open_in_outlook', message_id: selected.message_id });
      } else if (e.key === 'x' && selected) {
        e.preventDefault();
        toggleSelected(selected.id);
      } else if (e.key === 'g' && selected) {
        // g toggles cluster view (mnemonic for "group")
        e.preventDefault();
        setClusterMode((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hits, selected]);

  async function selectHit(h: SearchHit) {
    setSelected(h);
    setDetail(null);
    setDraft('');
    setAnalyzeOut(null);
    try {
      const e = await api.addinEmail(h.message_id);
      setDetail({
        body: e.body,
        body_unavailable: e.body_unavailable === true,
        classification_tier: e.classification_tier,
        classification_confidence: e.classification_confidence,
      });
      // Round 33: pass message_id so the brain can result-cache. Re-clicks
      // on the same email return instantly (no LLM call, $0).
      // Round 48: skip the auto-analyze when the body is gone — the LLM
      // would only answer "body appears to be empty" and burn a call.
      if (e.body_unavailable !== true) {
        runAnalyze(h.subject ?? '', h.from_addr, e.body, h.message_id);
      }
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof Error ? e.message : 'Could not load email',
      });
    }
  }

  async function runAnalyze(subject: string, from: string, body: string, messageId?: string) {
    setAnalyzeBusy(true);
    try {
      const r = await api.addinAnalyze(subject, from, body, undefined, messageId);
      setAnalyzeOut(r);
    } catch (e) {
      notifications.show({
        color: 'orange',
        message: friendlyAiError(e),
        autoClose: 8000,
      });
    } finally {
      setAnalyzeBusy(false);
    }
  }

  async function runDraft() {
    if (!selected || !detail) return;
    if (detail.body_unavailable) {
      notifications.show({ color: 'yellow', message: 'Body unavailable — cannot draft a reply for this email.' });
      return;
    }
    setDraftBusy(true);
    try {
      const r = await api.addinDraft(
        selected.subject ?? '',
        selected.from_addr,
        detail.body,
        analyzeOut?.suggested_reply_intent,
      );
      setDraft(r.draft);
    } catch (e) {
      notifications.show({
        color: 'red',
        message: friendlyAiError(e),
        autoClose: 8000,
      });
    } finally {
      setDraftBusy(false);
    }
  }

  // Round 34: structured composer — passes intent/tone/length/constraints.
  async function runDraftWithComposer(opts: {
    intent?: string;
    tone?: string;
    length?: string;
    user_intent?: string;
    constraints?: Record<string, boolean>;
  }) {
    if (!selected || !detail) return;
    if (detail.body_unavailable) {
      notifications.show({ color: 'yellow', message: 'Body unavailable — cannot draft a reply for this email.' });
      return;
    }
    setDraftBusy(true);
    try {
      const r = await api.addinDraftWithIntent({
        subject: selected.subject ?? '',
        from: selected.from_addr,
        body: detail.body,
        ...opts,
      });
      setDraft(r.draft);
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e), autoClose: 8000 });
    } finally {
      setDraftBusy(false);
    }
  }

  // Round 34: bulk multi-select operations.
  // Tracks the last toggled id for shift-click range selection.
  const lastToggledRef = useRef<string | null>(null);
  function toggleSelected(id: string, shift?: boolean) {
    if (shift && lastToggledRef.current && lastToggledRef.current !== id) {
      const ids = hits.map((h) => h.id);
      const a = ids.indexOf(lastToggledRef.current);
      const b = ids.indexOf(id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        const range = ids.slice(lo, hi + 1);
        // Range selection: shift-click extends, never shrinks the selection.
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const r of range) next.add(r);
          return next;
        });
        lastToggledRef.current = id;
        return;
      }
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    lastToggledRef.current = id;
  }
  async function bulkSnooze(hours: number) {
    const until = new Date(Date.now() + hours * 3600 * 1000).toISOString();
    const ids = Array.from(selectedIds);
    const idToHit = new Map(hits.map((h) => [h.id, h]));
    await Promise.all(
      ids.map((id) => {
        const h = idToHit.get(id);
        if (!h) return Promise.resolve();
        return api.addinSnooze(h.message_id, until).catch(() => {});
      }),
    );
    setHits((prev) => prev.filter((h) => !selectedIds.has(h.id)));
    setSelectedIds(new Set());
    notifications.show({ message: `Snoozed ${ids.length} email${ids.length === 1 ? '' : 's'}.` });
  }
  async function bulkDelete() {
    if (!confirm(`Delete ${selectedIds.size} email${selectedIds.size === 1 ? '' : 's'} permanently?`))
      return;
    const ids = Array.from(selectedIds);
    const idToHit = new Map(hits.map((h) => [h.id, h]));
    for (const id of ids) {
      const h = idToHit.get(id);
      if (!h) continue;
      postToHost({ type: 'delete_email', message_id: h.message_id });
    }
    setHits((prev) => prev.filter((h) => !selectedIds.has(h.id)));
    setSelectedIds(new Set());
    notifications.show({ message: `Deleted ${ids.length} emails.` });
  }
  function bulkMove(target: string) {
    const ids = Array.from(selectedIds);
    const idToHit = new Map(hits.map((h) => [h.id, h]));
    for (const id of ids) {
      const h = idToHit.get(id);
      if (!h) continue;
      postToHost({ type: 'move_email', message_id: h.message_id, target_folder: target });
    }
    setHits((prev) => prev.filter((h) => !selectedIds.has(h.id)));
    setSelectedIds(new Set());
    notifications.show({ message: `Moved ${ids.length} emails.` });
  }

  function openInOutlook() {
    if (!selected) return;
    postToHost({ type: 'open_in_outlook', message_id: selected.message_id });
  }

  async function sendAsReply() {
    if (!selected || !draft.trim()) return;
    const proceed = await runPreSendCheck({
      subject: selected.subject ?? '',
      to: selected.from_addr,
      body: draft,
    });
    if (!proceed) return;
    postToHost({
      type: 'send_as_reply',
      message_id: selected.message_id,
      subject: selected.subject ?? '',
      to: selected.from_addr,
      body: draft,
    });
    notifications.show({ message: 'Reply opened in Outlook with the AI draft.' });
  }

  function deleteSelected() {
    if (!selected) return;
    if (!confirm(`Delete this email permanently?\n\n${selected.subject ?? '(no subject)'}`))
      return;
    postToHost({ type: 'delete_email', message_id: selected.message_id });
    // Optimistically remove from results list.
    setHits((prev) => prev.filter((h) => h.id !== selected.id));
    setSelected(null);
    setDetail(null);
    setAnalyzeOut(null);
    notifications.show({ message: 'Email moved to Deleted Items.' });
  }

  function moveSelected(targetFolder: string) {
    if (!selected) return;
    postToHost({
      type: 'move_email',
      message_id: selected.message_id,
      target_folder: targetFolder,
    });
    notifications.show({ message: `Moved to ${targetFolder}.` });
    setHits((prev) => prev.filter((h) => h.id !== selected.id));
    setSelected(null);
    setDetail(null);
    setAnalyzeOut(null);
  }

  return (
    <Stack gap="sm">
      <SearchScopeBanner />
      <Group gap={4} wrap="nowrap">
        <TextInput
          size="md"
          placeholder='Search every indexed email…  (j/k to navigate, ? for help)'
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          rightSection={busy ? <Loader size="xs" /> : null}
          ref={queryInputRef}
          autoFocus
          style={{ flex: 1 }}
        />
        {queryHistory.length > 0 && (
          <Menu position="bottom-end">
            <Menu.Target>
              <ActionIcon size="lg" variant="default" aria-label="Query history">
                <IconHistory {...ICON_PROPS} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
              <Menu.Label>Recent searches</Menu.Label>
              {queryHistory.map((h) => (
                <Menu.Item key={h} onClick={() => setQ(h)}>{h}</Menu.Item>
              ))}
              <Menu.Divider />
              <Menu.Item color="red" onClick={() => {
                setQueryHistory([]);
                localStorage.removeItem('addin_search_query_history');
              }}>
                Clear history
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      <Group gap="xs">
        <Text size="xs" c="dimmed">Search in:</Text>
        <SegmentedControl
          size="xs"
          value={scope}
          onChange={(v) => setScope(v as 'all' | 'subject' | 'sender' | 'body')}
          data={[
            { value: 'all', label: 'All' },
            { value: 'subject', label: 'Subject' },
            { value: 'sender', label: 'Sender' },
            { value: 'body', label: 'Body' },
          ]}
        />
      </Group>

      <Group gap="xs" wrap="wrap">
        <Chip.Group multiple={false} value={filter} onChange={(v) => setFilter(v as string)}>
          {QUICK_FILTERS.map((f) => (
            <Chip key={f.value} value={f.value} color={(f as { color?: string }).color ?? 'blue'}>
              {f.label}
            </Chip>
          ))}
        </Chip.Group>
        <TextInput
          size="xs"
          placeholder="Filter by sender…"
          value={sender}
          onChange={(e) => setSender(e.currentTarget.value)}
          w={220}
        />
        {(filter !== 'all' || sender) && (
          <Button
            size="xs"
            variant="subtle"
            onClick={() => {
              setFilter('all');
              setSender('');
            }}
          >
            Clear filters
          </Button>
        )}
      </Group>

      {/* Date range + folder filter row */}
      <Group gap="xs" wrap="wrap">
        <TextInput
          size="xs"
          type="date"
          label="From"
          value={dateSince}
          onChange={(e) => setDateSince(e.currentTarget.value)}
          styles={{ label: { fontSize: 10, color: 'var(--mantine-color-dimmed)' } }}
        />
        <TextInput
          size="xs"
          type="date"
          label="To"
          value={dateUntil}
          onChange={(e) => setDateUntil(e.currentTarget.value)}
          styles={{ label: { fontSize: 10, color: 'var(--mantine-color-dimmed)' } }}
        />
        {(dateSince || dateUntil) && (
          <Button size="compact-xs" variant="subtle" mt={14}
            onClick={() => { setDateSince(''); setDateUntil(''); }}>
            Clear range
          </Button>
        )}
      </Group>

      {/* Per-folder sub-filter — top folders in current results */}
      {(() => {
        if (hits.length < 5) return null;
        const fcounts = new Map<string, number>();
        for (const h of hits) {
          if (!h.folder) continue;
          fcounts.set(h.folder, (fcounts.get(h.folder) ?? 0) + 1);
        }
        const top = [...fcounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
        if (top.length < 2) return null;
        return (
          <Group gap={4} wrap="wrap">
            <Text size="xs" c="dimmed">Folder:</Text>
            <Badge size="xs" variant={folderFilter === null ? 'filled' : 'light'} color="blue"
              style={{ cursor: 'pointer' }} onClick={() => setFolderFilter(null)}>
              Any
            </Badge>
            {top.map(([f, n]) => (
              <Badge key={f} size="xs"
                variant={folderFilter === f ? 'filled' : 'light'} color="blue"
                style={{ cursor: 'pointer', maxWidth: 200 }}
                onClick={() => setFolderFilter(folderFilter === f ? null : f)}>
                <Text size="xs" lineClamp={1}>{f} {n}</Text>
              </Badge>
            ))}
          </Group>
        );
      })()}

      {/* Active filters breadcrumb — chips with × to clear each. */}
      {(q.trim() || filter !== 'all' || sender.trim() || scope !== 'all') && (
        <Group gap={4} wrap="wrap">
          <Text size="xs" c="dimmed">Filters:</Text>
          {q.trim() && (
            <Badge size="sm" variant="light" color="blue"
              rightSection={
                <ActionIcon size={14} variant="transparent" color="blue" onClick={() => setQ('')}>
                  <IconX size={10} />
                </ActionIcon>
              }
            >
              q: {q.length > 24 ? `${q.slice(0, 24)}…` : q}
            </Badge>
          )}
          {filter !== 'all' && (
            <Badge size="sm" variant="light" color="grape"
              rightSection={
                <ActionIcon size={14} variant="transparent" color="grape" onClick={() => setFilter('all')}>
                  <IconX size={10} />
                </ActionIcon>
              }
            >
              {filter}
            </Badge>
          )}
          {sender.trim() && (
            <Badge size="sm" variant="light" color="teal"
              rightSection={
                <ActionIcon size={14} variant="transparent" color="teal" onClick={() => setSender('')}>
                  <IconX size={10} />
                </ActionIcon>
              }
            >
              from: {sender.length > 24 ? `${sender.slice(0, 24)}…` : sender}
            </Badge>
          )}
          {scope !== 'all' && (
            <Badge size="sm" variant="light" color="indigo"
              rightSection={
                <ActionIcon size={14} variant="transparent" color="indigo" onClick={() => setScope('all')}>
                  <IconX size={10} />
                </ActionIcon>
              }
            >
              scope: {scope}
            </Badge>
          )}
          <Button size="compact-xs" variant="subtle" color="gray"
            onClick={() => { setQ(''); setFilter('all'); setSender(''); setScope('all'); }}>
            Clear all
          </Button>
        </Group>
      )}

      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Text size="xs" c="dimmed" style={{ flex: 1, minWidth: 0 }}>
          {q.trim() === '' && filter === 'all' && !sender
            ? 'Type a query, pick a category chip, or filter by sender. Click a result to preview, triage, or draft a reply.'
            : hits.length === 0 && !busy
              ? 'No matches.'
              : `${hits.length.toLocaleString()} result${hits.length === 1 ? '' : 's'}${
                  searchTime != null ? ` in ${Math.round(searchTime)} ms` : ''
                }`}
        </Text>
        <Group gap={4}>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                size="compact-xs"
                variant="subtle"
                leftSection={<IconStar size={12} />}
                rightSection={presets.length > 0 ? <Badge size="xs" variant="light">{presets.length}</Badge> : null}
              >
                Presets
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconPlus size={14} />}
                onClick={savePreset}
                disabled={!q.trim() && filter === 'all' && !sender.trim() && scope === 'all'}
              >
                Save current as preset…
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileText size={14} />}
                onClick={exportPresets}
                disabled={presets.length === 0}
              >
                Export presets ({presets.length})
              </Menu.Item>
              <Menu.Item
                leftSection={<IconArrowBackUp size={14} />}
                onClick={importPresets}
              >
                Import presets…
              </Menu.Item>
              {presets.length > 0 && <Menu.Divider />}
              {presets.length === 0 && (
                <Menu.Item disabled>
                  <Text size="xs" c="dimmed">No saved presets yet.</Text>
                </Menu.Item>
              )}
              {presets.map((p) => (
                <Menu.Item
                  key={p.id}
                  rightSection={
                    <ActionIcon size="xs" variant="subtle" color="red"
                      onClick={(e) => { e.stopPropagation(); deletePreset(p.id); }}>
                      <IconX size={10} />
                    </ActionIcon>
                  }
                  onClick={() => applyPreset(p)}
                >
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>{p.name}</Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {[p.q && `q: ${p.q}`, p.filter !== 'all' && p.filter, p.sender && `from: ${p.sender}`, p.scope !== 'all' && `scope: ${p.scope}`]
                        .filter(Boolean).join(' · ') || 'no filters'}
                    </Text>
                  </Stack>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <Button
            size="compact-xs"
            variant={collapseDupes ? 'filled' : 'subtle'}
            color="grape"
            onClick={() => setCollapseDupes((v) => !v)}
          >
            Collapse dupes
          </Button>
          <Button
            size="compact-xs"
            variant="subtle"
            leftSection={<IconShare size={12} />}
            onClick={shareFilterUrl}
            disabled={!q.trim() && filter === 'all' && !sender.trim() && scope === 'all'}
          >
            Share
          </Button>
          <Button
            size="compact-xs"
            variant={clusterMode ? 'filled' : 'subtle'}
            color="indigo"
            leftSection={<IconUsersGroup size={12} />}
            onClick={() => setClusterMode((v) => !v)}
          >
            {clusterMode ? 'Hide clusters' : 'Cluster'}
          </Button>
        </Group>
      </Group>

      {clusterMode && (
        <ClusteredResultsCard
          data={clusterData}
          busy={clusterBusy}
          onOpenMessage={(mid) => {
            const h = hits.find((x) => x.message_id === mid);
            if (h) selectHit(h);
            else postToHost({ type: 'open_in_outlook', message_id: mid });
          }}
        />
      )}

      {/* Sender-mute hint: when a heavy machine sender dominates results,
          surface a one-click mute. Once muted (or dismissed), the banner
          stays hidden for the rest of the session. */}
      {(() => {
        if (hits.length < 5) return null;
        const counts = new Map<string, number>();
        for (const h of hits) {
          const k = h.from_addr.toLowerCase();
          if (mutedSenders.has(k) || dismissedSenders.has(k)) continue;
          counts.set(k, (counts.get(k) ?? 0) + 1);
        }
        const noisyRe = /no[-_]?reply|noreply|notifications?@|alerts?@|monitoring|automated/;
        const candidate = [...counts.entries()]
          .filter(([k, n]) => n >= 3 && noisyRe.test(k))
          .sort((a, b) => b[1] - a[1])[0];
        if (!candidate) return null;
        const [addr, count] = candidate;
        return (
          <Alert color="orange" variant="light" icon={<IconAlertTriangle size={14} />}
            withCloseButton
            onClose={() => setDismissedSenders((prev) => new Set(prev).add(addr))}>
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Text size="xs">
                <b>{addr}</b> dominates these results ({count} hits). Want to mute this sender?
              </Text>
              <Button size="compact-xs" color="orange" onClick={() => {
                // Optimistic local mute — banner hides + their hits drop
                // out immediately. Server is the source of truth for the
                // anti-VIP list, but we don't need to wait for it to render.
                setMutedSenders((prev) => new Set(prev).add(addr));
                api.addinAntiVipAdd(addr, 'search-banner').then(() => {
                  notifications.show({ color: 'orange', message: `Muted ${addr}. Future arrivals from this sender are silenced.` });
                }).catch((e) => {
                  // Roll back optimistic state on failure.
                  setMutedSenders((prev) => { const n = new Set(prev); n.delete(addr); return n; });
                  notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Mute failed' });
                });
              }}>
                Mute sender
              </Button>
            </Group>
          </Alert>
        );
      })()}

      <Group align="flex-start" gap="md" wrap="nowrap" style={{ minHeight: 480 }}>
        <Card padding="xs" radius="md" withBorder w="42%" style={{ minWidth: 280 }}>
          {selectedIds.size > 0 && (
            <Paper
              p="xs"
              withBorder
              mb={4}
              style={{ background: 'var(--mantine-color-blue-0)' }}
            >
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Text size="xs" fw={500}>
                  {selectedIds.size} selected
                </Text>
                <Group gap={4}>
                  <Button size="compact-xs" variant="default" onClick={() => bulkSnooze(1)}>
                    Snooze 1h
                  </Button>
                  <Button size="compact-xs" variant="default" onClick={() => bulkSnooze(24)}>
                    Tomorrow
                  </Button>
                  <Menu>
                    <Menu.Target>
                      <Button size="compact-xs" variant="default">
                        Move…
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown style={{ maxHeight: 280, overflowY: 'auto' }}>
                      {folderList.map((f) => (
                        <Menu.Item
                          key={f.path}
                          onClick={() => bulkMove(f.path)}
                          style={{ paddingLeft: 8 + f.depth * 12 }}
                        >
                          {f.name}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                  <BulkAddToCategoryMenu
                    selectedIds={selectedIds}
                    onApplied={() => setSelectedIds(new Set())}
                  />
                  {/* Bulk add to case — derive distinct cases from the
                      already-loaded membership map (no extra round-trip). */}
                  <Menu position="bottom-start">
                    <Menu.Target>
                      <Button size="compact-xs" variant="default" leftSection={<IconFolder size={12} />}>
                        Add to case…
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown style={{ maxHeight: 300, overflowY: 'auto' }}>
                      {allCases.length === 0 ? (
                        <Menu.Item disabled>
                          <Text size="xs" c="dimmed">No cases yet. Create one in the Cases tab.</Text>
                        </Menu.Item>
                      ) : (
                        allCases
                          .filter((c) => c.status === 'open')
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((c) => (
                            <Menu.Item
                              key={c.id}
                              leftSection={<IconFolder size={14} />}
                              onClick={async () => {
                                const ids = Array.from(selectedIds);
                                const idToHit = new Map(hits.map((h) => [h.id, h]));
                                const message_ids = ids
                                  .map((id) => idToHit.get(id)?.message_id)
                                  .filter((m): m is string => !!m);
                                if (message_ids.length === 0) return;
                                try {
                                  const r = await api.addinCaseAddMessages(c.id, message_ids);
                                  notifications.show({
                                    color: 'green',
                                    message: `Added ${r.added}/${message_ids.length} to ${c.name}.`,
                                  });
                                  setSelectedIds(new Set());
                                  // Refresh the membership map so case badges update.
                                  api.addinCasesMembership().then((rr) => {
                                    const m = new Map<string, { case_id: string; case_name: string; status: string }>();
                                    for (const x of rr.memberships) m.set(x.message_id, { case_id: x.case_id, case_name: x.case_name, status: x.status });
                                    setCaseMap(m);
                                  }).catch(() => {});
                                } catch (e) {
                                  notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
                                }
                              }}
                            >
                              <Stack gap={0}>
                                <Text size="sm">{c.name}</Text>
                                <Text size="xs" c="dimmed">{c.message_count} msg · {c.priority}</Text>
                              </Stack>
                            </Menu.Item>
                          ))
                      )}
                    </Menu.Dropdown>
                  </Menu>
                  <Button size="compact-xs" variant="default"
                    leftSection={<IconMail size={12} />}
                    onClick={() => {
                      const ids = Array.from(selectedIds);
                      const idToHit = new Map(hits.map((h) => [h.id, h]));
                      const rows = ids.map((id) => idToHit.get(id)).filter(Boolean) as SearchHit[];
                      if (rows.length > 20 && !confirm(`Open ${rows.length} emails in Outlook?`)) return;
                      for (const r of rows) postToHost({ type: 'open_in_outlook', message_id: r.message_id });
                      notifications.show({ message: `Opened ${rows.length} in Outlook.` });
                    }}>
                    Open all
                  </Button>
                  <Button size="compact-xs" variant="default"
                    leftSection={<IconFileText size={12} />}
                    onClick={() => {
                      const ids = Array.from(selectedIds);
                      const idToHit = new Map(hits.map((h) => [h.id, h]));
                      const rows = ids.map((id) => idToHit.get(id)).filter(Boolean) as SearchHit[];
                      const tsv = ['date\tsubject\tfrom\ttier\tmessage_id',
                        ...rows.map((r) => [r.received_at, (r.subject ?? '').replace(/\t/g, ' '),
                          r.from_addr, r.classification_tier ?? '', r.message_id].join('\t')),
                      ].join('\n');
                      navigator.clipboard.writeText(tsv)
                        .then(() => notifications.show({ message: `Copied ${rows.length} rows as table.` }))
                        .catch(() => notifications.show({ color: 'red', message: 'Copy failed' }));
                    }}>
                    Copy table
                  </Button>
                  <Button size="compact-xs" variant="default"
                    leftSection={<IconFileText size={12} />}
                    onClick={() => {
                      const ids = Array.from(selectedIds);
                      const idToHit = new Map(hits.map((h) => [h.id, h]));
                      const rows = ids.map((id) => idToHit.get(id)).filter(Boolean) as SearchHit[];
                      const esc = (s: string | null | undefined) => {
                        if (s == null) return '';
                        const v = String(s);
                        return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                      };
                      const lines = ['received_at,subject,from,tier,folder,message_id',
                        ...rows.map((r) => [r.received_at, r.subject, r.from_addr,
                          r.classification_tier, r.folder, r.message_id].map(esc).join(',')),
                      ];
                      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `search-hits-${new Date().toISOString().slice(0, 10)}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                    }}>
                    CSV
                  </Button>
                  <Button size="compact-xs" color="red" variant="light" onClick={bulkDelete}>
                    Delete
                  </Button>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Clear
                  </Button>
                </Group>
              </Group>
            </Paper>
          )}
          <ScrollArea h={selectedIds.size > 0 ? 480 : 520}>
            <Stack gap={2}>
              {(() => {
                if (!collapseDupes) return hits;
                // Collapse exact-subject + sender within 24h.
                const seen = new Map<string, SearchHit & { dup_count?: number }>();
                for (const h of hits) {
                  const day = h.received_at.slice(0, 10);
                  const k = `${(h.subject ?? '').toLowerCase().trim()}|${h.from_addr.toLowerCase()}|${day}`;
                  const cur = seen.get(k);
                  if (!cur) seen.set(k, { ...h, dup_count: 1 });
                  else cur.dup_count = (cur.dup_count ?? 1) + 1;
                }
                return [...seen.values()];
              })().map((h) => (
                <SearchRow
                  key={h.id}
                  hit={h}
                  active={selected?.id === h.id}
                  multiSelected={selectedIds.has(h.id)}
                  onClick={() => selectHit(h)}
                  onToggleSelect={(shift) => toggleSelected(h.id, shift)}
                  {...(onOpenSender !== undefined ? { onOpenSender } : {})}
                  risk={riskMap.get(h.message_id) ?? null}
                  caseInfo={caseMap.get(h.message_id) ?? null}
                />
              ))}
              {hits.length === 0 && !busy && (
                <Text c="dimmed" size="sm" p="md">
                  No matches.
                </Text>
              )}
            </Stack>
          </ScrollArea>
        </Card>

        <Card padding="md" radius="md" withBorder style={{ flex: 1 }}>
          {selected ? (
            <Stack gap="sm">
              <Stack gap={2}>
                <Text fw={600} size="lg" lineClamp={2}>
                  {selected.subject || '(no subject)'}
                </Text>
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" c="dimmed">
                    {selected.from_addr}
                  </Text>
                  <Text size="sm" c="dimmed">
                    ·
                  </Text>
                  <Text size="sm" c="dimmed">
                    {shortDate(selected.received_at)}
                  </Text>
                  {(analyzeOut?.tier ?? detail?.classification_tier) && (
                    <Badge color={tierColor(analyzeOut?.tier ?? detail?.classification_tier)}>
                      {analyzeOut?.tier ?? detail?.classification_tier}
                      {analyzeOut?.confidence != null
                        ? `  ${Math.round(analyzeOut.confidence * 100)}%`
                        : ''}
                    </Badge>
                  )}
                  {analyzeOut?.sentiment && (
                    <Badge variant="light" color={sentimentColor(analyzeOut.sentiment)}>
                      {analyzeOut.sentiment}
                    </Badge>
                  )}
                  {analyzeOut?.needs_reply && (
                    <Badge variant="filled" color="grape">
                      needs reply
                    </Badge>
                  )}
                </Group>
                {analyzeOut?.categories && analyzeOut.categories.length > 0 && (
                  <Group gap={4} mt={2}>
                    {analyzeOut.categories.map((c) => (
                      <Badge key={c} size="xs" variant="dot">
                        {c}
                      </Badge>
                    ))}
                  </Group>
                )}
              </Stack>

              {/* AI summary at the top — what the email is, in one glance */}
              {analyzeBusy && (
                <Alert color="blue" variant="light" icon={<Loader size="xs" />}>
                  Analyzing…
                </Alert>
              )}
              {analyzeOut?.summary && (
                <Alert color="blue" variant="light">
                  <Stack gap={4}>
                    <Group gap="xs" justify="space-between">
                      <Text size="sm" fw={500}>
                        Summary
                      </Text>
                      {analyzeOut._cache?.hit ? (
                        <Tooltip label="Already analyzed earlier — served from cache, $0 cost">
                          <Badge size="xs" color="green" variant="light">
                            cached · $0
                          </Badge>
                        </Tooltip>
                      ) : analyzeOut._cache?.cost_usd != null ? (
                        <Tooltip
                          label={`Fresh AI call via ${analyzeOut._cache.model}. Cost: $${analyzeOut._cache.cost_usd.toFixed(4)}`}
                        >
                          <Badge size="xs" color="gray" variant="light">
                            ${analyzeOut._cache.cost_usd.toFixed(4)}
                          </Badge>
                        </Tooltip>
                      ) : null}
                    </Group>
                    <Text size="sm">{analyzeOut.summary}</Text>
                    {analyzeOut.latest_ask && (
                      <Text size="sm" mt={2}>
                        <b>Latest ask:</b> {analyzeOut.latest_ask}
                      </Text>
                    )}
                    {analyzeOut.reasons && analyzeOut.reasons.length > 0 && (
                      <Stack gap={0} mt={4}>
                        <Text size="xs" c="dimmed" fw={500}>
                          Why:
                        </Text>
                        {analyzeOut.reasons.map((r, i) => (
                          <Text key={i} size="xs" c="dimmed">
                            · {r}
                          </Text>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Alert>
              )}
              {analyzeOut?.security_signals && analyzeOut.security_signals.length > 0 && (
                <Alert color="red" variant="light" icon={<IconShield {...ICON_PROPS} />}>
                  <Stack gap={2}>
                    <Text size="sm" fw={600}>
                      Security signals detected
                    </Text>
                    {analyzeOut.security_signals.map((s) => (
                      <Text key={s} size="xs">
                        · {s.replace(/_/g, ' ')}
                      </Text>
                    ))}
                    <Text size="xs" c="dimmed" mt={2}>
                      Verify out-of-band before taking any action this email asks for.
                    </Text>
                  </Stack>
                </Alert>
              )}

              {/* Action items extracted */}
              {analyzeOut?.action_items && analyzeOut.action_items.length > 0 && (
                <Card padding="sm" radius="sm" withBorder style={{ background: 'var(--mantine-color-grape-0)' }}>
                  <Text size="sm" fw={600} mb={4}>
                    Action items ({analyzeOut.action_items.length})
                  </Text>
                  <Stack gap={2}>
                    {analyzeOut.action_items.map((a, i) => (
                      <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                        <Badge
                          size="xs"
                          color={
                            a.priority === 'high'
                              ? 'red'
                              : a.priority === 'medium'
                                ? 'orange'
                                : 'gray'
                          }
                          style={{ flexShrink: 0, marginTop: 4 }}
                        >
                          {a.owner}
                        </Badge>
                        <Stack gap={0} style={{ flex: 1 }}>
                          <Text size="sm">{a.text}</Text>
                          {a.due && (
                            <Text size="xs" c="dimmed">
                              due {a.due}
                            </Text>
                          )}
                        </Stack>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              )}

              {analyzeOut?.key_entities && analyzeOut.key_entities.length > 0 && (
                <Group gap={4}>
                  <Text size="xs" c="dimmed">
                    Key:
                  </Text>
                  {analyzeOut.key_entities.slice(0, 8).map((k) => (
                    <Badge key={k} size="xs" variant="outline">
                      {k}
                    </Badge>
                  ))}
                </Group>
              )}

              <Group gap="xs" wrap="wrap" align="flex-start">
                <ReplyComposer
                  onDraft={(opts) => runDraftWithComposer(opts)}
                  loading={draftBusy}
                  disabled={!detail}
                  messageId={selected?.message_id}
                />
                <Button size="xs" variant="default" onClick={openInOutlook}>
                  Open in Outlook
                </Button>
                {draft.trim() && (
                  <Button size="xs" variant="light" color="green" onClick={sendAsReply}>
                    Send draft as Outlook reply
                  </Button>
                )}
                <SnoozeButton
                  onSnooze={(until, reason) => {
                    api.addinSnooze(selected.message_id, until, reason).then(() => {
                      notifications.show({
                        message: `Snoozed until ${new Date(until).toLocaleString()}.`,
                      });
                      setHits((prev) => prev.filter((h) => h.id !== selected.id));
                      setSelected(null);
                    });
                  }}
                />
                <Menu position="bottom-end" withArrow>
                  <Menu.Target>
                    <Button size="xs" variant="default">
                      Move to…
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown
                    style={{ maxHeight: 320, overflowY: 'auto' }}
                  >
                    {folderList.length === 0 ? (
                      <Menu.Item disabled>Loading folders…</Menu.Item>
                    ) : (
                      folderList.map((f) => (
                        <Menu.Item
                          key={f.path}
                          onClick={() => moveSelected(f.path)}
                          style={{ paddingLeft: 8 + f.depth * 12 }}
                        >
                          {f.name}
                        </Menu.Item>
                      ))
                    )}
                  </Menu.Dropdown>
                </Menu>
                <Button size="xs" color="red" variant="light" onClick={deleteSelected}>
                  Delete
                </Button>
                {onOpenSender && selected.from_addr && (
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => onOpenSender(selected.from_addr)}
                  >
                    Sender profile
                  </Button>
                )}
                <AddToCaseButton messageId={selected.message_id} />
                <AttachmentsBadge messageId={selected.message_id} />
              </Group>

              <SecurityBadge messageId={selected.message_id} />

              <Divider />

              {detail?.body_unavailable ? (
                <Alert color="yellow" title="Body unavailable">
                  The content of this email is no longer stored — it expired
                  per retention or was deleted from the mail server before it
                  could be indexed. Subject, sender and classification are
                  still available. / لم يعد نص هذه الرسالة متوفراً.
                </Alert>
              ) : (
                <ScrollArea h={180}>
                  <Text
                    size="sm"
                    c="dimmed"
                    style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                  >
                    {detail?.body ?? 'Loading email…'}
                  </Text>
                </ScrollArea>
              )}

              {(draftBusy || draft) && (
                <>
                  <Divider />
                  <Stack gap={4}>
                    <Text fw={600} size="sm">
                      AI-drafted reply
                    </Text>
                    <Textarea
                      autosize
                      minRows={4}
                      maxRows={12}
                      value={draft}
                      onChange={(e) => setDraft(e.currentTarget.value)}
                      placeholder={draftBusy ? 'Generating…' : ''}
                    />
                  </Stack>
                </>
              )}
            </Stack>
          ) : (
            <Center h={480}>
              <Text c="dimmed">Click a result to preview.</Text>
            </Center>
          )}
        </Card>
      </Group>

      <Modal opened={helpOpen} onClose={() => setHelpOpen(false)} title="Search keyboard shortcuts" size="sm">
        <Stack gap="xs">
          <Group justify="space-between"><Text size="sm">Focus query</Text><Code>/</Code></Group>
          <Group justify="space-between"><Text size="sm">Move down</Text><Code>j</Code></Group>
          <Group justify="space-between"><Text size="sm">Move up</Text><Code>k</Code></Group>
          <Group justify="space-between"><Text size="sm">Open in Outlook</Text><Code>Enter</Code></Group>
          <Group justify="space-between"><Text size="sm">Toggle bulk select</Text><Code>x</Code></Group>
          <Group justify="space-between"><Text size="sm">Cmd/Ctrl-click row → Outlook</Text><Code>⌘ click</Code></Group>
          <Group justify="space-between"><Text size="sm">Middle-click row → Outlook</Text><Code>middle click</Code></Group>
          <Group justify="space-between"><Text size="sm">Right-click row → menu</Text><Code>right click</Code></Group>
          <Group justify="space-between"><Text size="sm">Toggle clusters</Text><Code>g</Code></Group>
          <Group justify="space-between"><Text size="sm">Show this help</Text><Code>?</Code></Group>
          <Text size="xs" c="dimmed" mt="xs">Shortcuts ignore typing in inputs.</Text>
        </Stack>
      </Modal>
    </Stack>
  );
}

export function SearchRow({
  hit,
  active,
  multiSelected,
  onClick,
  onToggleSelect,
  onOpenSender,
  risk,
  caseInfo,
}: {
  hit: SearchHit;
  active: boolean;
  multiSelected?: boolean;
  onClick: () => void;
  onToggleSelect?: (shift?: boolean) => void;
  onOpenSender?: (email: string) => void;
  risk?: { score: number; verdict: 'clean' | 'suspicious' | 'dangerous'; top_signal: string } | null;
  caseInfo?: { case_id: string; case_name: string; status: string } | null;
}): React.JSX.Element {
  const subj = cleanSubject(hit.subject);
  const [menuOpen, setMenuOpen] = useState(false);

  function snooze(hours: number) {
    const until = new Date(Date.now() + hours * 3600 * 1000).toISOString();
    api.addinSnooze(hit.message_id, until).then(() => {
      notifications.show({ message: `Snoozed ${hours}h.` });
    }).catch((e) => {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Snooze failed' });
    });
    setMenuOpen(false);
  }
  function snoozeMonday() {
    const d = new Date();
    const days = (8 - d.getDay()) % 7 || 7;
    d.setDate(d.getDate() + days);
    d.setHours(9, 0, 0, 0);
    api.addinSnooze(hit.message_id, d.toISOString()).then(() => {
      notifications.show({ message: 'Snoozed until Monday 9am.' });
    }).catch((e) => {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Snooze failed' });
    });
    setMenuOpen(false);
  }
  function muteSender() {
    api.addinAntiVipAdd(hit.from_addr, 'context-menu').then(() => {
      notifications.show({ color: 'orange', message: `Muted ${hit.from_addr}` });
    }).catch((e) => {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Mute failed' });
    });
    setMenuOpen(false);
  }

  return (
    <Menu
      opened={menuOpen}
      onChange={setMenuOpen}
      position="bottom-start"
      withinPortal
      shadow="md"
    >
    <Menu.Target>
    <Paper
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenuOpen(true);
      }}
      onAuxClick={(e) => {
        // Middle-click → open in Outlook directly. Skips preview, stays in list.
        if (e.button === 1) {
          e.preventDefault();
          postToHost({ type: 'open_in_outlook', message_id: hit.message_id });
        }
      }}
      onClickCapture={(e) => {
        // ⌘/Ctrl-click on row → open in Outlook without disturbing preview.
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          e.stopPropagation();
          postToHost({ type: 'open_in_outlook', message_id: hit.message_id });
        }
      }}
      style={{
        cursor: 'pointer',
        padding: 8,
        borderLeft: active ? '3px solid var(--mantine-color-blue-6)' : '3px solid transparent',
        background: active
          ? 'var(--mantine-color-blue-0)'
          : multiSelected
            ? 'var(--mantine-color-blue-1)'
            : undefined,
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="xs" align="center">
        {onToggleSelect && (
          <Checkbox
            size="xs"
            checked={!!multiSelected}
            onChange={(e) => {
              e.stopPropagation();
              // Native checkbox onChange doesn't carry shift state cleanly;
              // we read it from the underlying mouse event (nativeEvent).
              const ev = e.nativeEvent as MouseEvent;
              onToggleSelect(ev?.shiftKey === true);
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label="select for bulk"
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }} onClick={onClick}>
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Text size="xs" c="dimmed" w={70} style={{ flexShrink: 0 }}>
              {shortDate(hit.received_at)}
            </Text>
            {hit.classification_tier && (
              <Badge color={tierColor(hit.classification_tier)} size="xs" style={{ flexShrink: 0 }}>
                {hit.classification_tier}
              </Badge>
            )}
            {(hit.dup_count ?? 1) > 1 && (
              <Tooltip label={`${hit.dup_count} identical messages collapsed`}>
                <Badge size="xs" color="grape" variant="light" style={{ flexShrink: 0 }}>
                  ×{hit.dup_count}
                </Badge>
              </Tooltip>
            )}
            {risk && (
              <Tooltip label={`${risk.verdict.toUpperCase()} (${risk.score}) — ${risk.top_signal}`}>
                <Badge
                  size="xs"
                  color={risk.verdict === 'dangerous' ? 'red' : 'orange'}
                  variant="filled"
                  leftSection={<IconShield size={9} />}
                  style={{ flexShrink: 0 }}
                >
                  {risk.score}
                </Badge>
              </Tooltip>
            )}
            {caseInfo && (
              <Tooltip label={`Case: ${caseInfo.case_name} (${caseInfo.status})`}>
                <Badge
                  size="xs"
                  color={caseInfo.status === 'open' ? 'blue' : caseInfo.status === 'closed' ? 'gray' : 'orange'}
                  variant="light"
                  leftSection={<IconFolder size={9} />}
                  style={{ flexShrink: 0, maxWidth: 100, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    sessionStorage.setItem('addin_open_case', caseInfo.case_id);
                    window.dispatchEvent(new Event('ai-outlook:open-case'));
                  }}
                >
                  <Text size="xs" lineClamp={1}>{caseInfo.case_name}</Text>
                </Badge>
              </Tooltip>
            )}
            {/* Hover preview: shows the snippet body without requiring a click. */}
            <Tooltip
              label={hit.snippet || '(no body indexed)'}
              multiline
              w={460}
              openDelay={500}
              withinPortal
              position="bottom-start"
              disabled={!hit.snippet}
            >
              <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                <Group gap={4} wrap="nowrap">
                  {subj.prefix && (
                    <Badge size="xs" color="gray" variant="outline" style={{ flexShrink: 0 }}>
                      {subj.prefix}
                    </Badge>
                  )}
                  {subj.tags.slice(0, 2).map((t) => (
                    <Badge key={t} size="xs" color="grape" variant="light" style={{ flexShrink: 0 }}>
                      {t}
                    </Badge>
                  ))}
                  <Text fw={500} size="sm" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                    {subj.clean}
                  </Text>
                </Group>
                <Text
                  size="xs"
                  c="dimmed"
                  lineClamp={1}
                  onClick={(e) => {
                    if (onOpenSender) {
                      e.stopPropagation();
                      onOpenSender(hit.from_addr);
                    }
                  }}
                  style={{ cursor: onOpenSender ? 'pointer' : undefined }}
                >
                  {hit.from_addr}
                </Text>
              </Stack>
            </Tooltip>
          </Group>
        </div>
      </Group>
    </Paper>
    </Menu.Target>
    <Menu.Dropdown>
      <Menu.Label>Open</Menu.Label>
      <Menu.Item leftSection={<IconMail size={14} />}
        onClick={() => { postToHost({ type: 'open_in_outlook', message_id: hit.message_id }); setMenuOpen(false); }}>
        Open in Outlook
      </Menu.Item>
      {onOpenSender && (
        <Menu.Item leftSection={<IconUser size={14} />}
          onClick={() => { onOpenSender(hit.from_addr); setMenuOpen(false); }}>
          Sender profile
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Label>AI</Menu.Label>
      <Menu.Item leftSection={<IconPencil size={14} />}
        onClick={() => {
          window.dispatchEvent(new CustomEvent('ai-outlook:draft-reply', { detail: { message_id: hit.message_id, subject: hit.subject } }));
          setMenuOpen(false);
        }}>
        Draft AI reply
      </Menu.Item>
      <Menu.Item leftSection={<IconMessageQuestion size={14} />}
        onClick={() => {
          window.dispatchEvent(new CustomEvent('ai-outlook:ask-ai', { detail: { message_id: hit.message_id, subject: hit.subject } }));
          setMenuOpen(false);
        }}>
        Ask AI about this email
      </Menu.Item>
      <Menu.Divider />
      <Menu.Label>Remind / followup</Menu.Label>
      <Menu.Item leftSection={<IconBell size={14} />}
        onClick={() => {
          const at = new Date(Date.now() + 3_600_000).toISOString();
          api.addinReminderCreate({ message_id: hit.message_id, subject: hit.subject ?? '', remind_at: at })
            .then(() => notifications.show({ color: 'green', message: 'Reminder set in 1h' }))
            .catch((e) => notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'reminder failed' }));
          setMenuOpen(false);
        }}>
        Remind me in 1h
      </Menu.Item>
      <Menu.Item leftSection={<IconBell size={14} />}
        onClick={() => {
          const t = new Date();
          t.setDate(t.getDate() + 1); t.setHours(9, 0, 0, 0);
          api.addinReminderCreate({ message_id: hit.message_id, subject: hit.subject ?? '', remind_at: t.toISOString() })
            .then(() => notifications.show({ color: 'green', message: 'Reminder set for tomorrow 9am' }));
          setMenuOpen(false);
        }}>
        Remind me tomorrow 9am
      </Menu.Item>
      <Menu.Item leftSection={<IconFlag size={14} />}
        onClick={() => {
          const note = prompt('Followup note for this email:', hit.subject ?? '');
          if (!note) { setMenuOpen(false); return; }
          api.addinUserFollowupCreate({
            message_id: hit.message_id, ...(hit.subject != null ? { subject: hit.subject } : {}),
            from_addr: hit.from_addr, note,
          })
            .then(() => notifications.show({ color: 'green', message: 'Followup added' }));
          setMenuOpen(false);
        }}>
        Add followup…
      </Menu.Item>
      <Menu.Divider />
      <Menu.Label>Snooze</Menu.Label>
      <Menu.Item leftSection={<IconClock size={14} />} onClick={() => snooze(1)}>1 hour</Menu.Item>
      <Menu.Item leftSection={<IconClock size={14} />} onClick={() => snooze(24)}>Tomorrow morning</Menu.Item>
      <Menu.Item leftSection={<IconClock size={14} />} onClick={snoozeMonday}>Monday 9am</Menu.Item>
      {onToggleSelect && (
        <>
          <Menu.Divider />
          <Menu.Item leftSection={multiSelected ? <IconX size={14} /> : <IconCheck size={14} />}
            onClick={() => { onToggleSelect(); setMenuOpen(false); }}>
            {multiSelected ? 'Remove from selection' : 'Add to bulk selection'}
          </Menu.Item>
        </>
      )}
      <Menu.Divider />
      <Menu.Item color="red" leftSection={<IconArchive size={14} />} onClick={muteSender}>
        Mute sender (anti-VIP)
      </Menu.Item>
    </Menu.Dropdown>
    </Menu>
  );
}

// =========================================================================
// Triage — work the currently-selected Outlook email.
// =========================================================================

export function TriagePanel({ email }: { email: OutlookEmailContext | null }): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [analyzeOut, setAnalyzeOut] = useState<Awaited<ReturnType<typeof api.addinAnalyze>> | null>(
    null,
  );
  const [draft, setDraft] = useState('');
  // Per-draft instruction + tone + length. Empty instruction falls back to
  // the analyzer's suggested_reply_intent (the existing one-click behavior),
  // so users who liked the silent default see no regression. Filled
  // instruction takes over as `user_intent` with `intent: 'free'` so the
  // brain uses the user's literal request as the primary goal of the draft.
  const [draftInstruction, setDraftInstruction] = useState('');
  const [draftTone, setDraftTone] = useState('balanced');
  const [draftLength, setDraftLength] = useState('medium');

  // Auto-run analysis whenever the selected email changes.
  useEffect(() => {
    setAnalyzeOut(null);
    setDraft('');
    if (!email?.body) return;
    let cancelled = false;
    setBusy(true);
    api
      .addinAnalyze(email.subject ?? '', email.from ?? '', email.body)
      .then((r) => {
        if (!cancelled) setAnalyzeOut(r);
      })
      .catch(() => {
        /* leave analyzeOut null */
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email?.message_id]);

  async function runDraft() {
    if (!email?.body) return;
    setDraftBusy(true);
    try {
      const trimmedInstruction = draftInstruction.trim();
      const r = trimmedInstruction
        ? await api.addinDraftWithIntent({
            subject: email.subject ?? '',
            from: email.from ?? '',
            body: email.body,
            intent: 'free',
            user_intent: trimmedInstruction,
            tone: draftTone,
            length: draftLength,
          })
        : await api.addinDraft(
            email.subject ?? '',
            email.from ?? '',
            email.body,
            analyzeOut?.suggested_reply_intent,
          );
      setDraft(r.draft);
    } catch (e) {
      notifications.show({
        color: 'red',
        message: friendlyAiError(e),
        autoClose: 8000,
      });
    } finally {
      setDraftBusy(false);
    }
  }

  async function sendAsReply() {
    if (!email || !draft.trim()) return;
    const proceed = await runPreSendCheck({
      subject: email.subject ?? '',
      to: email.from ?? '',
      body: draft,
    });
    if (!proceed) return;
    postToHost({
      type: 'send_as_reply',
      message_id: email.message_id,
      entry_id: email.entry_id,
      subject: email.subject ?? '',
      to: email.from ?? '',
      body: draft,
    });
    notifications.show({ message: 'Reply opened in Outlook with the AI draft.' });
  }

  function refresh() {
    postToHost({ type: 'request_selected_email' });
  }

  if (!email || !email.subject) {
    return (
      <Card padding="xl" radius="md" withBorder>
        <Stack align="center" gap="xs">
          <Text fw={600}>No email selected</Text>
          <Text size="sm" c="dimmed" ta="center">
            Select an email in Outlook, then click any ai-outlook ribbon button. The form
            auto-pulls whatever is highlighted.
          </Text>
          <Button size="xs" variant="default" onClick={refresh}>
            Refresh from Outlook
          </Button>
        </Stack>
      </Card>
    );
  }

  function deleteEmail() {
    if (!email) return;
    if (!confirm(`Delete this email permanently?\n\n${email.subject}`)) return;
    postToHost({ type: 'delete_email', message_id: email.message_id, entry_id: email.entry_id });
    notifications.show({ message: 'Email moved to Deleted Items.' });
  }

  return (
    <Stack gap="sm">
      <Card padding="md" radius="md" withBorder>
        <Stack gap="xs">
          <Text fw={600} size="lg">
            {email.subject}
          </Text>
          <Group gap="xs" wrap="wrap">
            <Text size="sm" c="dimmed">
              {email.from}
            </Text>
            <Text size="sm" c="dimmed">
              ·
            </Text>
            <Text size="sm" c="dimmed">
              {shortDate(email.received_at)}
            </Text>
            {email.folder_path && (
              <Tooltip label="Outlook folder + store this email lives in. Helps diagnose why an email isn't appearing in search.">
                <Badge size="xs" variant="light" color="cyan" style={{ cursor: 'help' }}>
                  {email.store_name ? `${email.store_name} · ` : ''}{email.folder_path}
                </Badge>
              </Tooltip>
            )}
            {analyzeOut && (
              <Badge color={tierColor(analyzeOut.tier)}>
                {analyzeOut.tier} · {Math.round(analyzeOut.confidence * 100)}%
              </Badge>
            )}
            {analyzeOut?.sentiment && (
              <Badge variant="light" color={sentimentColor(analyzeOut.sentiment)}>
                {analyzeOut.sentiment}
              </Badge>
            )}
            {analyzeOut?.needs_reply && (
              <Badge variant="filled" color="grape">
                needs reply
              </Badge>
            )}
          </Group>
          {analyzeOut?.categories && analyzeOut.categories.length > 0 && (
            <Group gap={4}>
              {analyzeOut.categories.map((c) => (
                <Badge key={c} size="xs" variant="dot">
                  {c}
                </Badge>
              ))}
            </Group>
          )}
          <Card padding="sm" radius="sm" withBorder bg="var(--mantine-color-gray-0)">
            <Stack gap={6}>
              <Group justify="space-between" align="flex-end" gap="xs">
                <Text size="sm" fw={600}>
                  AI Draft Reply
                </Text>
                <Text size="xs" c="dimmed">
                  Leave instruction blank to let the AI infer from the email
                  ({analyzeOut?.suggested_reply_intent ?? 'auto'}).
                </Text>
              </Group>
              <Textarea
                placeholder="How do you want to reply? e.g. &quot;Thank them, confirm Tuesday works, and ask for the agenda.&quot;"
                value={draftInstruction}
                onChange={(e) => setDraftInstruction(e.currentTarget.value)}
                autosize
                minRows={2}
                maxRows={5}
                disabled={draftBusy}
              />
              <Group gap="xs" grow wrap="nowrap">
                <NativeSelect
                  size="xs"
                  label="Tone"
                  value={draftTone}
                  onChange={(e) => setDraftTone(e.currentTarget.value)}
                  data={[
                    { value: 'balanced', label: 'Professional (balanced)' },
                    { value: 'formal', label: 'Formal' },
                    { value: 'warm', label: 'Friendly / warm' },
                    { value: 'short_direct', label: 'Short and direct' },
                  ]}
                  disabled={draftBusy}
                />
                <NativeSelect
                  size="xs"
                  label="Length"
                  value={draftLength}
                  onChange={(e) => setDraftLength(e.currentTarget.value)}
                  data={[
                    { value: 'short', label: 'Short (2–3 lines)' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'long', label: 'Detailed' },
                  ]}
                  disabled={draftBusy}
                />
              </Group>
              <Group gap="xs">
                <Button onClick={runDraft} loading={draftBusy} size="sm">
                  {draft ? 'Regenerate' : 'Generate Draft'}
                </Button>
                {draftInstruction.length > 0 && (
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => setDraftInstruction('')}
                    disabled={draftBusy}
                  >
                    Clear instruction
                  </Button>
                )}
              </Group>
            </Stack>
          </Card>
          <Group gap="xs">
            <Button variant="default" size="sm" onClick={refresh}>
              Refresh
            </Button>
            <Button variant="light" color="red" size="sm" onClick={deleteEmail}>
              Delete
            </Button>
          </Group>
          {busy && (
            <Alert color="blue" variant="light" icon={<Loader size="xs" />}>
              Analyzing email…
            </Alert>
          )}
          {analyzeOut?.summary && (
            <Alert color="blue" variant="light">
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  Summary
                </Text>
                <Text size="sm">{analyzeOut.summary}</Text>
              </Stack>
            </Alert>
          )}
          {analyzeOut?.action_items && analyzeOut.action_items.length > 0 && (
            <Card
              padding="sm"
              radius="sm"
              withBorder
              style={{ background: 'var(--mantine-color-grape-0)' }}
            >
              <Text size="sm" fw={600} mb={4}>
                Action items ({analyzeOut.action_items.length})
              </Text>
              <Stack gap={2}>
                {analyzeOut.action_items.map((a, i) => (
                  <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                    <Badge
                      size="xs"
                      color={
                        a.priority === 'high'
                          ? 'red'
                          : a.priority === 'medium'
                            ? 'orange'
                            : 'gray'
                      }
                      style={{ flexShrink: 0, marginTop: 4 }}
                    >
                      {a.owner}
                    </Badge>
                    <Stack gap={0} style={{ flex: 1 }}>
                      <Text size="sm">{a.text}</Text>
                      {a.due && (
                        <Text size="xs" c="dimmed">
                          due {a.due}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                ))}
              </Stack>
            </Card>
          )}
          {analyzeOut?.key_entities && analyzeOut.key_entities.length > 0 && (
            <Group gap={4}>
              <Text size="xs" c="dimmed">
                Key:
              </Text>
              {analyzeOut.key_entities.slice(0, 8).map((k) => (
                <Badge key={k} size="xs" variant="outline">
                  {k}
                </Badge>
              ))}
            </Group>
          )}
        </Stack>
      </Card>

      <Card padding="md" radius="md" withBorder>
        <Stack gap="xs">
          <Text fw={600}>Email body</Text>
          <ScrollArea h={180}>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} c="dimmed">
              {email.body || '(no body)'}
            </Text>
          </ScrollArea>
        </Stack>
      </Card>

      {(draftBusy || draft) && (
        <Card padding="md" radius="md" withBorder>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text fw={600}>AI-drafted reply</Text>
              {draft.trim() && (
                <Button size="xs" color="green" onClick={sendAsReply}>
                  Send as Outlook reply
                </Button>
              )}
            </Group>
            <Textarea
              autosize
              minRows={5}
              maxRows={16}
              value={draft}
              onChange={(e) => setDraft(e.currentTarget.value)}
              placeholder={draftBusy ? 'Generating…' : ''}
            />
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

// =========================================================================
// Brief — executive daily summary, all in one view.
// =========================================================================

// Round 28-AA: Briefing as a morning intelligence brief — not an email list.
//
// Earlier reframes (8-row "Today, you must" list with right-click menus)
// were still treating Briefing as inventory. Real exec workflow: don't
// triage 50 individual emails — make 5 decisions on the threads that
// matter, with AI-drafted replies ready to approve/edit/send.
//
// This panel is now powered by /addin/morning-narrative which:
//   1. Groups acting-state messages by thread_id
//   2. Generates an AI draft reply per thread (top 5)
//   3. Composes a 3–5-sentence narrative tying it together
//   4. Surfaces last-24h risks + an "AI did overnight" stats line
//
// Decisions, not data entry.
